import { useContext, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus } from '../../../assets';
import { UserContext } from '../../../components/shared/useUser';
import { ListPageTemplate } from '../../../components/page-templates/list-page/ListPageTemplate';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import MenusListPageContent from './components/MenusListPageContent';

const MenusPage = () => {
  const { t } = useTranslation('range');
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermissionTo } = useContext(UserContext);
  const [currentFilterSearchTerm, setCurrentFilterSearchTerm] = useState('');
  const [currentFilterDisplayStatus, setCurrentFilterDisplayStatus] = useState('');
  const [currentFilterMenuType, setCurrentFilterMenuType] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const canView = hasPermissionTo(['MenuRead']);
  const canCreate = hasPermissionTo(['MenuWrite']);
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setCurrentFilterSearchTerm(searchParams.get('searchTerm') || '');
    setCurrentFilterDisplayStatus(searchParams.get('displayStatus') || '');
    setCurrentFilterMenuType(searchParams.get('menuType') || '');
  }, [location.search]);

  useEffect(() => {
    const message = (location.state as { successMessage?: string } | null)?.successMessage;
    if (!message) return;

    setSuccessMessage(message);
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [location.pathname, location.search, location.state, navigate]);

  const handleFilterChange = (filters: { searchTerm?: string; displayStatus?: string; menuType?: string } = {}) => {
    const nextSearch = filters.searchTerm ?? currentFilterSearchTerm;
    const nextStatus = filters.displayStatus ?? currentFilterDisplayStatus;
    const nextMenuType = filters.menuType ?? currentFilterMenuType;
    setCurrentFilterSearchTerm(nextSearch);
    setCurrentFilterDisplayStatus(nextStatus);
    setCurrentFilterMenuType(nextMenuType);

    const params = new URLSearchParams();
    if (nextSearch) params.set('searchTerm', nextSearch);
    if (nextStatus) params.set('displayStatus', nextStatus);
    if (nextMenuType) params.set('menuType', nextMenuType);
    navigate(`?${params.toString()}`, { replace: true });
  };

  if (!canView) return null;

  return (
    <>
      <MenusListPageContent
        onFilterChange={handleFilterChange}
        initialSearchTerm={currentFilterSearchTerm}
        initialDisplayStatus={currentFilterDisplayStatus}
        initialMenuType={currentFilterMenuType}
      >
        {({ filterBar, advancedFilterContent, advancedFilterActions, headlineContent, table, isInitialLoading }) => (
          <ListPageTemplate
            title={t('menusPageTitle')}
            subtitle={t('menusPageSubtitle')}
            loading={isInitialLoading}
            loadingLabel={t('loading')}
            actions={canCreate && (
              <Button
                variant="primary"
                size="lg"
                startIcon={<Plus />}
                onClick={() => navigate('/range/menus/new')}
              >
                {t('createMenu')}
              </Button>
            )}
            headlineContent={headlineContent}
            filterContent={filterBar}
            advancedFilterContent={advancedFilterContent}
            advancedFilterActions={advancedFilterActions}
            table={table}
          />
        )}
      </MenusListPageContent>
      <MessagesSnackbar
        open={Boolean(successMessage)}
        onClose={() => setSuccessMessage(null)}
        message={successMessage ?? ''}
        severity="success"
        duration={4000}
      />
    </>
  );
};

export default MenusPage;
